<script setup lang="ts">
import { computed } from 'vue';
import { ListFilterIcon, UsersIcon } from 'lucide-vue-next';
import { DataTableFilter } from '@/components/shared/DataTable/data-table';
import type { FilterOption } from '@/components/shared/DataTable/data-table';

type Filter = { key: string; value: string };

const props = withDefaults(
  defineProps<{
    filters: Filter[];
    eligibleReviewers?: string[];
  }>(),
  { eligibleReviewers: () => [] }
);

const emit = defineEmits<{ 'update-filter': [filters: Record<string, Filter>] }>();

const statusFilter = computed(() => props.filters.find(f => f.key === 'status'));
const pipelineReviewerFilter = computed(() => props.filters.find(f => f.key === 'pipeline_reviewer'));

const statusValue = computed(() => statusFilter.value?.value || 'All');
const reviewerValue = computed(() => pipelineReviewerFilter.value?.value || 'All');

const statusLabel = computed(() => (statusValue.value === 'All' ? 'All statuses' : `Status: ${statusValue.value}`));
const reviewerLabel = computed(() => (reviewerValue.value === 'All' ? 'All reviewers' : reviewerValue.value));

const statusOptions: FilterOption[] = [
  { value: 'All', label: 'All' },
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const reviewerOptions = computed<FilterOption[]>(() => [
  { value: 'All', label: 'All reviewers' },
  ...props.eligibleReviewers.map(reviewer => ({ value: reviewer, label: reviewer })),
]);

const buildFilters = (changes: { status?: string; pipeline_reviewer?: string }): Record<string, Filter> => ({
  status: { key: 'status', value: changes.status || statusValue.value },
  pipeline_reviewer: { key: 'pipeline_reviewer', value: changes.pipeline_reviewer || reviewerValue.value },
});
</script>

<template>
  <div class="flex flex-wrap items-center gap-4">
    <DataTableFilter
      label="Teammate status"
      :trigger-label="statusLabel"
      :icon="ListFilterIcon"
      :options="statusOptions"
      :model-value="statusValue"
      testid="team-list-status-filter"
      @select="value => emit('update-filter', buildFilters({ status: value }))"
    />

    <DataTableFilter
      label="Pipeline reviewer"
      :trigger-label="reviewerLabel"
      :icon="UsersIcon"
      :options="reviewerOptions"
      :model-value="reviewerValue"
      width="w-64"
      testid="team-list-reviewer-filter"
      @select="value => emit('update-filter', buildFilters({ pipeline_reviewer: value }))"
    />
  </div>
</template>
